import {
  IBay40sMisplacedPair,
  bay40sLocationValidatorFn,
} from "../baysData/bay40sLocationValidator";
import {
  IDerivedMinBottomHeight,
  IOpenVesselDefinitionV1,
  moveBay40sToLocation,
} from "open-vessel-definition";
import { cloneObject, createDictionary } from "@tedivo/tedivo-pure-helpers";

export interface IFixBay40sLocationResult {
  /** The vessel with its 40'+ data in place. The input object when nothing changed */
  json: IOpenVesselDefinitionV1;
  /** The pairs whose 40'+ data was moved to the right half */
  moved: IBay40sMisplacedPair[];
  /** Pairs reported by the validator that could not be moved (a half is missing) */
  notMoved: IBay40sMisplacedPair[];
  /**
   * Rows where the file states a `minBottomHeight` lower than the geometry of the two
   * bays allows. Reported by the mover, left untouched.
   */
  minBottomHeightConflicts: IDerivedMinBottomHeight[];
  changed: boolean;
}

/**
 * Moves the 40'+ definitions that `bay40sLocationValidator` reports in the wrong half
 * of a pair into the right one. The input is not modified.
 *
 * Everything attached to a 40'+ length travels with it (the slot sizes, the
 * `infoByContLength` CGs and the per-row info), merged gap-filling: what the target
 * Bay already defines wins. A source slot left with no sizes becomes `restricted`,
 * as the Bay editor's "Move 40'+ definitions" helper does.
 */
export function fixBay40sLocation(json: IOpenVesselDefinitionV1): IFixBay40sLocationResult {
  const validation = bay40sLocationValidatorFn({
    data: { json, lcgVcgTcgAndPairings: undefined, lcgsBy20Bay: undefined },
  });

  const misplaced = validation.result.misplaced;

  if (misplaced.length === 0) {
    return { json, moved: [], notMoved: [], minBottomHeightConflicts: [], changed: false };
  }

  const newJson = cloneObject(json);
  const baysDict = createDictionary(newJson.baysData, (bl) => `${bl.isoBay}-${bl.level}`);

  const moved: IBay40sMisplacedPair[] = [];
  const notMoved: IBay40sMisplacedPair[] = [];
  const minBottomHeightConflicts: IDerivedMinBottomHeight[] = [];

  misplaced.forEach((pair) => {
    const fwdBl = baysDict[`${pair.fwd}-${pair.level}`];
    const aftBl = baysDict[`${pair.aft}-${pair.level}`];

    if (!fwdBl || !aftBl) {
      notMoved.push(pair);
      return;
    }

    // Only this pair is handed over, so the vessel's other pairs are never touched
    const res = moveBay40sToLocation([fwdBl, aftBl], {
      bay40sLocation: pair.to,
      mutate: true,
      setRestrictedOnEmptySlots: true,
      deriveMinBottomHeights: false,
    });

    if (res.movedPairs.length > 0) moved.push(pair);
    else notMoved.push(pair);

    minBottomHeightConflicts.push(...res.minBottomHeightConflicts);
  });

  const changed = moved.length > 0;

  return {
    json: changed ? newJson : json,
    moved,
    notMoved,
    minBottomHeightConflicts,
    changed,
  };
}
